// TagPicker — multi-select tag picker with inline create.
//
// CHG-1325 / ITER-3452 — Tag taxonomy phase 3 (FE integration).
//
// Used by EntityTagsSection on every taggable-entity detail page
// (donor / campaign / recurring_donation / pledge / donation / message).
// Multi-select with substring search; renders each tag as a colored
// chip; "Create new tag" inline option that calls tagsApi.createTag
// when no existing tag matches the typed search.
//
// Permission gating:
//   - Picker visibility: caller wraps in PermissionGate(tag.read) per
//     entity-type read.
//   - attach / detach buttons: gated on tag.attach / tag.detach via
//     useHasPermission. When the user lacks the permission the button
//     renders disabled with the standard denied tooltip.
//   - "Create new tag" inline option: gated on tag.create; hidden when
//     the user lacks the permission.
//
// Slot contract (props):
//   - allTags: current full tag inventory for the org (caller owns the
//     listTags query so the picker can be reused without refetching).
//   - selectedTagIds: ids the entity currently has attached.
//   - onAttach(tagId): caller wires this to tagsApi.attachTag.
//   - onDetach(tagId): caller wires this to tagsApi.detachTag.
//   - onCreate(name, color): caller wires this to tagsApi.createTag.
//
// The picker is presentational — no React Query inside. Caller owns
// mutations + cache invalidation so refresh policy can vary per
// embedding context.

import { useMemo, useState } from "react";
import { useHasPermission } from "../../auth/usePermissions";
import type { Tag } from "../../api/types";

export interface TagPickerProps {
  allTags: Tag[];
  selectedTagIds: string[];
  // Return values are ignored by TagPicker — callbacks may return any
  // mutation result so callers can pass useMutation.mutateAsync directly
  // without a void-wrapping shim.
  onAttach: (tagId: string) => unknown | Promise<unknown>;
  onDetach: (tagId: string) => unknown | Promise<unknown>;
  /** Inline create. If omitted, the "Create new tag" affordance is hidden. */
  onCreate?: (name: string, color: string) => unknown | Promise<unknown>;
  placeholder?: string;
  /** Default color for inline-created tags. Matches the T2 server_default
   * `#6366F1` (indigo-500) per CHG-1305 tag.entity.yaml. */
  defaultNewTagColor?: string;
  disabled?: boolean;
}

const ATTACH_PERM = "alora-giving.tag.attach";
const DETACH_PERM = "alora-giving.tag.detach";
const CREATE_PERM = "alora-giving.tag.create";

const DEFAULT_NEW_COLOR = "#6366F1";

const DENIED_TOOLTIP = "You don't have permission to perform this action";

// Chip rendering — keeps the visual contract consistent with how
// tags are rendered elsewhere (color swatch + name).
function TagChip({
  tag,
  onRemove,
  removable,
  removeDisabled,
  removeDeniedTooltip,
}: {
  tag: Tag;
  onRemove?: () => void;
  removable: boolean;
  removeDisabled?: boolean;
  removeDeniedTooltip?: boolean;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px 2px 4px",
        borderRadius: 999,
        background: "var(--color-bg-subtle)",
        border: "1px solid var(--color-border)",
        fontSize: 12,
      }}
      data-tag-id={tag.id}
    >
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: tag.color,
          border: "1px solid var(--color-border)",
        }}
        title={tag.color}
      />
      <span>{tag.name}</span>
      {removable && onRemove ? (
        <button
          type="button"
          onClick={removeDisabled ? undefined : onRemove}
          aria-label={`Detach ${tag.name}`}
          disabled={removeDisabled}
          title={removeDeniedTooltip ? DENIED_TOOLTIP : `Detach ${tag.name}`}
          data-permission-gate-state={removeDeniedTooltip ? "denied" : undefined}
          style={{
            background: "transparent",
            border: "none",
            cursor: removeDisabled ? "not-allowed" : "pointer",
            color: "var(--color-text-muted)",
            fontSize: 14,
            lineHeight: 1,
            padding: 0,
            opacity: removeDisabled ? 0.6 : 1,
          }}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

export function TagPicker({
  allTags,
  selectedTagIds,
  onAttach,
  onDetach,
  onCreate,
  placeholder = "Add a tag…",
  defaultNewTagColor = DEFAULT_NEW_COLOR,
  disabled = false,
}: TagPickerProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const canAttach = useHasPermission(ATTACH_PERM);
  const canDetach = useHasPermission(DETACH_PERM);
  const canCreate = useHasPermission(CREATE_PERM);

  const selectedSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);
  const selectedTags = useMemo(
    () => allTags.filter((t) => selectedSet.has(t.id)),
    [allTags, selectedSet],
  );

  // Substring (case-insensitive) match on name. Excludes already-selected.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTags.filter(
      (t) => !selectedSet.has(t.id) && (q === "" || t.name.toLowerCase().includes(q)),
    );
  }, [allTags, selectedSet, query]);

  const trimmed = query.trim();
  // Exact-match suppresses the inline-create option even if the user
  // typed it (prevents "Create new tag 'donors'" when a 'donors' tag
  // already exists).
  const exactMatch = allTags.some(
    (t) => t.name.toLowerCase() === trimmed.toLowerCase(),
  );
  const canShowCreate =
    canCreate &&
    onCreate !== undefined &&
    trimmed.length > 0 &&
    !exactMatch;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
      data-testid="tag-picker"
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minHeight: 28 }}>
        {selectedTags.map((tag) => (
          <TagChip
            key={tag.id}
            tag={tag}
            removable
            onRemove={() => onDetach(tag.id)}
            removeDisabled={disabled || !canDetach}
            removeDeniedTooltip={!canDetach}
          />
        ))}
        {selectedTags.length === 0 ? (
          <span style={{ color: "var(--color-text-muted)", fontSize: 12 }}>
            No tags yet
          </span>
        ) : null}
      </div>

      <div style={{ position: "relative" }}>
        <input
          type="text"
          className="input"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Delay close so click on dropdown row fires first.
            window.setTimeout(() => setOpen(false), 150);
          }}
          aria-label="Search or create tag"
          style={{ width: "100%", maxWidth: 360 }}
        />
        {open && (matches.length > 0 || canShowCreate) ? (
          <div
            role="listbox"
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              maxWidth: 360,
              marginTop: 4,
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              boxShadow: "var(--shadow-card)",
              maxHeight: 240,
              overflowY: "auto",
              zIndex: 50,
            }}
          >
            {matches.map((tag) => {
              const denied = !canAttach;
              return (
                <button
                  key={tag.id}
                  type="button"
                  role="option"
                  aria-selected="false"
                  disabled={denied}
                  title={denied ? DENIED_TOOLTIP : `Add ${tag.name}`}
                  data-permission-gate-state={denied ? "denied" : undefined}
                  onMouseDown={(e) => {
                    // Prevent input blur from firing first.
                    e.preventDefault();
                  }}
                  onClick={() => {
                    if (denied) return;
                    onAttach(tag.id);
                    setQuery("");
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    background: "transparent",
                    border: "none",
                    cursor: denied ? "not-allowed" : "pointer",
                    fontSize: 13,
                    opacity: denied ? 0.6 : 1,
                  }}
                >
                  <TagChip tag={tag} removable={false} />
                </button>
              );
            })}
            {canShowCreate ? (
              <button
                type="button"
                role="option"
                aria-selected="false"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onCreate?.(trimmed, defaultNewTagColor);
                  setQuery("");
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 12px",
                  background: "transparent",
                  border: "none",
                  borderTop: matches.length > 0 ? "1px solid var(--color-border-subtle)" : "none",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--color-primary)",
                }}
              >
                + Create new tag "{trimmed}"
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
